import { SHORT_LABEL_CHARS } from './constants';

// Which element a pointer or focus event belongs to, and what its tooltip
// says. Content comes from, in order: the element's own data-tip /
// data-tip-title, the central REGISTRY, then a native title="".

// How far up from the event target to look for a tip owner.
const MAX_DEPTH = 8;

export const hasOwnTip = (el) =>
  el.hasAttribute('data-tip') || el.hasAttribute('data-tip-title');

export const matchRegistry = (el, registry) => {
  for (let i = 0; i < registry.length; i += 1) {
    try {
      if (el.matches(registry[i].sel)) return registry[i];
    } catch (e) {
      // A bad selector in the registry never breaks hovering.
    }
  }
  return null;
};

// A native title moves off the element while its tooltip shows, so the
// browser's own tooltip doesn't appear as well. It comes back when the tip
// hides (unless the page has set a new one in the meantime).
const stash = new WeakMap();

export const stashedTitle = (el) => (stash.has(el) ? stash.get(el) : null);

export const stashTitle = (el) => {
  const title = el.getAttribute('title');
  if (title == null) return stashedTitle(el);
  el.removeAttribute('title');
  stash.set(el, title);
  // Icon-only buttons keep an accessible name.
  if (title && !el.hasAttribute('aria-label') && !el.textContent.trim()) {
    el.setAttribute('aria-label', title);
  }
  return title;
};

export const restoreTitle = (el) => {
  if (!stash.has(el)) return;
  const title = stash.get(el);
  stash.delete(el);
  if (!el.hasAttribute('title')) el.setAttribute('title', title);
};

const hasTitle = (el) => !!el.getAttribute('title') || !!stashedTitle(el);

export const findTipTarget = (node, registry) => {
  let el = node && node.nodeType === 1 ? node : node && node.parentElement;
  for (let depth = 0; el && depth < MAX_DEPTH; depth += 1) {
    const doc = el.ownerDocument;
    if (el === doc.body || el === doc.documentElement) return null;
    if (el.tagName === 'IFRAME') return null;
    if (el.hasAttribute('data-tip-off')) return null;
    if (hasOwnTip(el) || hasTitle(el) || matchRegistry(el, registry)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

// → { t, b, k, side } or null.
export const tipContentFor = (el, registry) => {
  if (hasOwnTip(el)) {
    return {
      t: el.getAttribute('data-tip-title'),
      b: el.getAttribute('data-tip'),
      k: el.getAttribute('data-tip-kbd'),
      side: el.getAttribute('data-tip-side'),
    };
  }
  const entry = matchRegistry(el, registry);
  if (entry) return entry;
  const title = el.getAttribute('title') || stashedTitle(el);
  return title ? { t: null, b: title, k: null, side: null } : null;
};

// A help tip has a title; everything else is a label.
export const isHelpTip = (content) => !!(content && content.t);

export const isShortLabel = (content) =>
  !isHelpTip(content) && String(content.b || '').length <= SHORT_LABEL_CHARS;

export const keysOf = (content) =>
  content && content.k ? String(content.k).split(' ').filter(Boolean) : [];

// aria-describedby is a token list; add and remove only our id.
export const addDescribedBy = (el, id) => {
  const ids = (el.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter(Boolean);
  if (ids.indexOf(id) === -1) ids.push(id);
  el.setAttribute('aria-describedby', ids.join(' '));
};

export const removeDescribedBy = (el, id) => {
  const ids = (el.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter((token) => token && token !== id);
  if (ids.length) el.setAttribute('aria-describedby', ids.join(' '));
  else el.removeAttribute('aria-describedby');
};
